import React, { useEffect, useState } from 'react';
import {CourseTypesList, EngagementTypesList} from '../../constants/Course';
import {CourseCategoriesInput, CourseCodesInput} from './Inputs';
import { PendingField } from './PendingField';
import { CourseContentInputs } from './CourseContentInputs';
import { CourseRevisionPanel, CourseHistoryPanel } from './CourseRevisionPanel';
import { CourseEditLayout, CourseEditColumn, CourseEditSection } from './CourseEditLayout';
import { SeoCourseEdit } from './SeoCourseEdit';
import { getUser } from '../../helper/user';
import { Link } from 'react-router-dom';
import { useFormState } from 'react-hook-form';
import {
  AutocompleteInput,
  BooleanInput,
  EditButton,
  FormDataConsumer,
  NumberInput,
  ReferenceInput,
  SelectInput,
  ShowButton,
  TextInput,
  TopToolbar,
  TranslatableInputs,
  useRecordContext,
  useRedirect,
  useGetIdentity,
} from 'react-admin';
import { EditGuesser, InputGuesser } from '@api-platform/admin';
import { Button } from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { isGranted } from '../../helper/security';
import { ROLES } from '../../constants/Roles';
import { SupplierInput } from '../Inputs/SupplierInput';

export const CourseEdit = (props) => getUser()?.roles?.includes(ROLES.ROLE_SEO_EDITOR)
  ? <SeoCourseEdit {...props} /> : <PublishedCourseEdit {...props} />;

const PublishedCourseEdit = (props) => {
  const [iconImageChanged, setIconImageChanged] = useState(false);
  const [featuredImageChanged, setFeaturedImageChanged] = useState(false);
  const [featuredImageMobileChanged, setFeaturedImageMobileChanged] =
    useState(false);
  const [featuredImageListChanged, setFeaturedImageListChanged] =
    useState(false);
  const [featuredImageListMobileChanged, setFeaturedImageListMobileChanged] =
    useState(false);

  const [description, setDescription] = useState('');
  const [seoTitle, setSeoTitle] = useState('');

  const transform = (data, { previousData } = {}) => {
    data['code'] = data.code.trim();

    if (
      previousData?.code &&
      data.code !== previousData.code &&
      !window.confirm(
        `Change course code from "${previousData.code}" to "${data.code}"? ` +
          'This will also update linked Drupal content.'
      )
    ) {
      throw new Error('Course code change cancelled.');
    }

    data['basePrice'] = data.basePrice.toString();

    if (
      data['iconImage']['mediaObject'] === null ||
      data['iconImage']['mediaObject'].length === 0
    ) {
      data['iconImage'] = null;
    }

    if (
      data['featuredImage']['mediaObject'] === null ||
      data['featuredImage']['mediaObject'].length === 0
    ) {
      data['featuredImage'] = null;
    }

    if (
      data['featuredImageMobile']['mediaObject'] === null ||
      data['featuredImageMobile']['mediaObject'].length === 0
    ) {
      data['featuredImageMobile'] = null;
    }

    if (
      data['featuredImageList']['mediaObject'] === null ||
      data['featuredImageList']['mediaObject'].length === 0
    ) {
      data['featuredImageList'] = null;
    }

    if (
      data['featuredImageListMobile']['mediaObject'] === null ||
      data['featuredImageListMobile']['mediaObject'].length === 0
    ) {
      data['featuredImageListMobile'] = null;
    }

    // data['showFeaturedImageOnList'] = data['showFeaturedImageOnList'].filter(
    //   (e) => e === 'desktop' || e === 'mobile'
    // );
    // data['showFeaturedImageOnPage'] = data['showFeaturedImageOnPage'].filter(
    //   (e) => e === 'desktop' || e === 'mobile'
    // );
    // data['showIconImageOnList'] = data['showIconImageOnList'].filter(
    //   (e) => e === 'desktop' || e === 'mobile'
    // );

    data['iconImageUpdated'] = iconImageChanged;
    data['featuredImageUpdated'] = featuredImageChanged;
    data['featuredImageMobileUpdated'] = featuredImageMobileChanged;
    data['featuredImageListUpdated'] = featuredImageListChanged;
    data['featuredImageListMobileUpdated'] = featuredImageListMobileChanged;

    data['translations'] = {
      de: {
        '@id': '/course_translations/' + data.translationsFormView.id.de,
        title: data.translationsFormView.title.de,
        subtitle: data.translationsFormView.subtitle.de,
        description: data.translationsFormView.description.de,
        descriptionLong: data.translationsFormView.descriptionLong.de,
        descriptionShort: data.translationsFormView.descriptionShort.de,
        descriptionShortForList: data.translationsFormView
            .descriptionShortForList.de,
        requirements: data.translationsFormView.requirements.de,
        certificationContent: data.translationsFormView.certificationContent.de,
        targetGroup: data.translationsFormView.targetGroup.de,
        targetGroupShort: data.translationsFormView.targetGroupShort.de,
        objectives: data.translationsFormView.objectives.de,
        exam: data.translationsFormView.exam.de,
        documents: data.translationsFormView.documents.de,
        keywords: data.translationsFormView.keywords.de,
        content: data.translationsFormView.content.de,
        showUntilText: data.translationsFormView.showUntilText.de,
        requirementsShort: data.translationsFormView.requirementsShort.de,
        salary: data.translationsFormView.salary.de,
        reviewsByTitle: data.translationsFormView.reviewsByTitle.de,
        vacancies: data.translationsFormView.vacancies.de,
        ausbildungInfo: data.translationsFormView.ausbildungInfo.de,
        ausbildungFaq: data.translationsFormView.ausbildungFaq.de,
        ausbildungAdditionalService:
          data.translationsFormView.ausbildungAdditionalService.de,
        ausbildungAfter: data.translationsFormView.ausbildungAfter.de,
        seoTitle: data.translationsFormView.seoTitle.de,
        seoDescription: data.translationsFormView.seoDescription.de,
        seoKeywords: data.translationsFormView.seoKeywords.de,
        educationSubsidy: data.translationsFormView.educationSubsidy.de,
        subsidy: data.translationsFormView.subsidy.de,
        subsidyShort: data.translationsFormView.subsidyShort.de,
        educationGraduationTitle:
          data.translationsFormView.educationGraduationTitle.de,
        educationTeachingTime:
          data.translationsFormView.educationTeachingTime.de,
        educationPromotion: data.translationsFormView.educationPromotion.de,
        educationSpace: data.translationsFormView.educationSpace.de,
        educationLabourMarketChances:
          data.translationsFormView.educationLabourMarketChances.de,
        educationRatio: data.translationsFormView.educationRatio.de,
        examCodes: data.translationsFormView.examCodes.de,
        eduGoal: data.translationsFormView.eduGoal.de,
        graduationType: data.translationsFormView.graduationType.de,
        teachingType: data.translationsFormView.teachingType.de,
        executionType: data.translationsFormView.executionType.de,
        otherLocations: data.translationsFormView.otherLocations.de,
        locale: 'de',
      },
      // en: {
      //   '@id': '/course_translations/' + data.translationsFormView.id.en,
      //   title: data.translationsFormView.title.en,
      //   subtitle: data.translationsFormView.subtitle.en,
      //   description: data.translationsFormView.description.en,
      //   descriptionShort: data.translationsFormView.descriptionShort.en,
      //   descriptionShortForList:
      //     data.translationsFormView.descriptionShortForList.en,
      //   requirements: data.translationsFormView.requirements.en,
      //certificationContent: data.translationsFormView.certificationContent.en,
      //   targetGroup: data.translationsFormView.targetGroup.en,
      //   targetGroupShort: data.translationsFormView.targetGroupShort.en,
      //   objectives: data.translationsFormView.objectives.en,
      //   exam: data.translationsFormView.exam.en,
      //   documents: data.translationsFormView.documents.en,
      //   keywords: data.translationsFormView.keywords.en,
      //   content: data.translationsFormView.content.en,
      //   showUntilText: data.translationsFormView.showUntilText.en,
      //   ausbildungInfo: data.translationsFormView.ausbildungInfo.en,
      //   ausbildungFaq: data.translationsFormView.ausbildungFaq.en,
      //   ausbildungAdditionalService:
      //     data.translationsFormView.ausbildungAdditionalService.en,
      //   ausbildungAfter: data.translationsFormView.ausbildungAfter.en,
      //   seoTitle: data.translationsFormView.seoTitle.en,
      //   seoDescription: data.translationsFormView.seoDescription.en,
      //   seoKeywords: data.translationsFormView.seoKeywords.en,
      //   educationSubsidy: data.translationsFormView.educationSubsidy.en,
      //   subsidy: data.translationsFormView.subsidy.en,
      //   subsidyShort: data.translationsFormView.subsidyShort.en,
      //   educationGraduationTitle:
      //     data.translationsFormView.educationGraduationTitle.en,
      //   educationTeachingTime:
      //     data.translationsFormView.educationTeachingTime.en,
      //   educationPromotion: data.translationsFormView.educationPromotion.en,
      //   educationSpace: data.translationsFormView.educationSpace.en,
      //   educationLabourMarketChances:
      //     data.translationsFormView.educationLabourMarketChances.en,
      //   educationRatio: data.translationsFormView.educationRatio.en,
      //   examCodes: data.translationsFormView.examCodes.en,
      //   eduGoal: data.translationsFormView.eduGoal.en,
      //   graduationType: data.translationsFormView.graduationType.en,
      //   teachingType: data.translationsFormView.teachingType.en,
      //   executionType: data.translationsFormView.executionType.en,
      //   otherLocations: data.translationsFormView.otherLocations.en,
      //   locale: 'en',
      // },
    };

    return data;
  };

  return (
    <EditGuesser
      {...props}
      actions={<CourseActions isOnEditPage={true} />}
      transform={transform}
    >
      <EditInputs
        iconImageChanged={iconImageChanged}
        handleIconImageChange={setIconImageChanged}
        featuredImageChanged={featuredImageChanged}
        featuredImageMobileChanged={featuredImageMobileChanged}
        featuredImageListChanged={featuredImageListChanged}
        featuredImageListMobileChanged={featuredImageListMobileChanged}
        handleFeaturedImageChange={setFeaturedImageChanged}
        handleFeaturedImageMobileChange={setFeaturedImageMobileChanged}
        handleFeaturedImageListChange={setFeaturedImageListChanged}
        handleFeaturedImageListMobileChange={setFeaturedImageListMobileChanged}
      />
    </EditGuesser>
  );
};

export const EditInputs = (props) => {
  const [lockedFields, setLockedFields] = useState(null);
  const { dirtyFields } = useFormState();
  const { data, isLoading, error } = useGetIdentity();
  const record = useRecordContext();
  const redirect = useRedirect();

  useEffect(() => {
    if (!record) return;
    const canCourseCreatorEdit =
      data?.roles?.includes(ROLES.ROLE_COURSE_CREATOR) &&
      (
        record.editableByCourseCreator ||
        (
          record.createdByEmail === data.username &&
          !record.enabled
        )
      );

    if (
      !isGranted(ROLES.ROLE_ADMIN, data) &&
      !isGranted(ROLES.ROLE_ADDER_AND_EDIT_OWN, data) &&
      !canCourseCreatorEdit
    ) {
      redirect('show', 'courses', record.id);
    }
  }, [record, data, redirect]);

  const {
    iconImageChanged,
    featuredImageChanged,
    featuredImageMobileChanged,
    featuredImageListChanged,
    featuredImageListMobileChanged,
    handleIconImageChange,
    handleFeaturedImageChange,
    handleFeaturedImageMobileChange,
    handleFeaturedImageListChange,
    handleFeaturedImageListMobileChange,
  } = props;

  useEffect(() => {
    if (dirtyFields['iconImage']) {
      handleIconImageChange(true);
    } else {
      handleIconImageChange(false);
    }

    if (dirtyFields['featuredImage']) {
      handleFeaturedImageChange(true);
    } else {
      handleFeaturedImageChange(false);
    }

    if (dirtyFields['featuredImageMobile']) {
      handleFeaturedImageMobileChange(true);
    } else {
      handleFeaturedImageMobileChange(false);
    }

    if (dirtyFields['featuredImageList']) {
      handleFeaturedImageListChange(true);
    } else {
      handleFeaturedImageListChange(false);
    }

    if (dirtyFields['featuredImageListMobile']) {
      handleFeaturedImageListMobileChange(true);
    } else {
      handleFeaturedImageListMobileChange(false);
    }
  });

  if (!record) return null;

  return (
    <CourseEditLayout>
      {isGranted(ROLES.ROLE_ADMIN, data) && <CourseEditSection><CourseRevisionPanel courseId={record.id} onLockedFields={setLockedFields} /></CourseEditSection>}
      <CourseEditColumn>
        <ReferenceInput
          source="company"
          reference="companies"
          label="Company"
          filterToQuery={(searchText) => ({ name: searchText })}
        >
          <SelectInput
            disabled
            fullWidth
            source="company"
            optionText="name"
            id="company"
          />
        </ReferenceInput>
        <InputGuesser
          fullWidth
          required
          source={'code'}
          id=""
          helperText={
            'Changing the code also updates the linked Drupal course, course ' +
            'cities, reviews and courses that aggregate its reviews.'
          }
        />
        <SelectInput
          fullWidth
          required
          source="type"
          choices={CourseTypesList}
          id="type"
        />
        <SelectInput
          fullWidth
          source="engagementType"
          choices={EngagementTypesList}
          id="engagementType"
        />
        <ReferenceInput
          source="iconImage.mediaObject"
          reference="media_objects"
          label="Icon"
        >
          <AutocompleteInput
            fullWidth
            optionText="fileName"
            filterToQuery={(q) => ({ fileName: q })}
            id="iconImage.mediaObject"
          />
        </ReferenceInput>
        <ReferenceInput
          source="featuredImage.mediaObject"
          reference="media_objects"
        >
          <AutocompleteInput
            fullWidth
            label="Featured Image Page"
            optionText="fileName"
            filterToQuery={(q) => ({ fileName: q })}
            id="featuredImage.mediaObject"
          />
        </ReferenceInput>
        <ReferenceInput
          source="featuredImageMobile.mediaObject"
          reference="media_objects"
        >
          <AutocompleteInput
            fullWidth
            label="Featured Image Page Mobile"
            optionText="fileName"
            filterToQuery={(q) => ({ fileName: q })}
            id="featuredImageMobile.mediaObject"
          />
        </ReferenceInput>
        <ReferenceInput
          source="featuredImageList.mediaObject"
          reference="media_objects"
        >
          <AutocompleteInput
            fullWidth
            label="Featured Image List"
            optionText="fileName"
            filterToQuery={(q) => ({ fileName: q })}
            id="featuredImageList.mediaObject"
          />
        </ReferenceInput>
        <ReferenceInput
          source="featuredImageListMobile.mediaObject"
          reference="media_objects"
        >
          <AutocompleteInput
            fullWidth
            label="Featured Image List Mobile"
            optionText="fileName"
            filterToQuery={(q) => ({ fileName: q })}
            id="featuredImageListMobile.mediaObject"
          />
        </ReferenceInput>
        <FormDataConsumer>
          {({ formData, ...rest }) => {
            return <CourseCategoriesInput formData={formData} {...rest} />;
          }}
        </FormDataConsumer>
        <InputGuesser fullWidth required source={'duration'} id="duration" />
        <SelectInput
          fullWidth
          required
          source="durationType"
          choices={[
            { id: 'hour', name: 'Stunden' },
            { id: 'day', name: 'Tage' },
            { id: 'week', name: 'Wochen' },
            { id: 'month', name: 'Monate' },
            { id: 'year', name: 'Jahre' },
          ]}
          id="durationType"
        />
        <NumberInput fullWidth required source={'basePrice'} id="basePrice" />
        <SupplierInput
          company={record.company}
          currentSupplier={record.supplier}
        />
        <InputGuesser
          fullWidth
          source={'preferredInList'}
          id="preferredInList"
        />
        <NumberInput
          fullWidth
          required
          source={'positionInList'}
          id="positionInList"
        />
        <NumberInput fullWidth required source={'priority'} id="priority" />
        <InputGuesser
          fullWidth
          source={'multipleOfficeVersions'}
          id="multipleOfficeVersions"
        />
        {!data?.roles?.includes(ROLES.ROLE_COURSE_CREATOR) && (
          <InputGuesser fullWidth source={'enabled'} id="enabled" />
        )}
        {isGranted(ROLES.ROLE_ADMIN, data) && (
          <BooleanInput
            fullWidth
            label="Freelancer can always edit"
            source="editableByCourseCreator"
          />
        )}
        <BooleanInput fullWidth label="Check" source="check" />
        <hr/>
        <h3>Aggregate reviews </h3>
        <TranslatableInputs
            locales={['de']}
            defaultLocale="de"
            groupKey="translations"
        >
        <PendingField lockedFields={isGranted(ROLES.ROLE_ADMIN, data) ? lockedFields : []}>
        <TextInput
            fullWidth
            label={'Title pattern'}
            source="translationsFormView.reviewsByTitle"
            id="translationsFormView.reviewsByTitle"
        />
        </PendingField>
        </TranslatableInputs>
        <FormDataConsumer>
          {({ formData, ...rest }) => {
            return <CourseCodesInput formData={formData} {...rest} />;
          }}
        </FormDataConsumer>
          <TextInput
              fullWidth
              multiline
              rows={4}
              label="Internal comment"
              source="internalComment"
              id="internalComment"
          />
      </CourseEditColumn>
      <CourseEditColumn>
        <CourseContentInputs lockedFields={isGranted(ROLES.ROLE_ADMIN, data) ? lockedFields : []} />
      </CourseEditColumn>
      {isGranted(ROLES.ROLE_ADMIN, data) && <CourseEditSection><CourseHistoryPanel courseId={record.id} /></CourseEditSection>}
    </CourseEditLayout>
  );
};

export const CourseActions = ({ isOnEditPage }) => {
  const record = useRecordContext();
  const { data } = useGetIdentity();

  if (!record) {
    return null;
  }

  const isCourseCreator = data?.roles?.includes(ROLES.ROLE_COURSE_CREATOR);
  const canCourseCreatorEdit =
    isCourseCreator &&
    (
      record.editableByCourseCreator ||
      (
        record.createdByEmail === data.username &&
        !record.enabled
      )
    );

  if (isCourseCreator) {
    return (
      <TopToolbar>
        {isOnEditPage && <ShowButton />}
        {!isOnEditPage && canCourseCreatorEdit && <EditButton />}
        {canCourseCreatorEdit && (
          <Button
            startIcon={<CalendarMonthIcon />}
            component={Link}
            color="primary"
            size={'small'}
            to={`/courses/${encodeURIComponent(record.id)}/calendar`}
            sx={{ lineHeight: '1.5' }}
          >
            Calendar
          </Button>
        )}
      </TopToolbar>
    );
  }

  return (
    <TopToolbar>
      {isOnEditPage ? <ShowButton /> : <EditButton />}
      <Button
        startIcon={<CalendarMonthIcon />}
        component={Link}
        color="primary"
        size={'small'}
        to={`/courses/${encodeURIComponent(record.id)}/calendar`}
        sx={{ lineHeight: '1.5' }}
      >
        Calendar
      </Button>
      <Button
        startIcon={<CalendarMonthIcon />}
        component={Link}
        color="primary"
        size={'small'}
        to={`/courses/${encodeURIComponent(record.id)}/clone`}
        sx={{ lineHeight: '1.5' }}
      >
        Clone
      </Button>
    </TopToolbar>
  );
};
